// Flowbite-based MetricCard component - Modern Tailwind design

import React from 'react';
import { TrendingUp, TrendingDown, Minus } from 'lucide-react';
import clsx from 'clsx';

interface MetricCardProps {
  title: string;
  value: string | number;
  trend?: number;
  icon?: React.ReactNode;
  subtitle?: string;
  loading?: boolean;
  onClick?: () => void;
  variant?: 'default' | 'success' | 'warning' | 'danger';
  className?: string;
}

export const MetricCard: React.FC<MetricCardProps> = ({
  title,
  value,
  trend,
  icon,
  subtitle,
  loading = false,
  onClick,
  variant = 'default',
  className
}) => {
  const isClickable = !!onClick;
  const trendDirection = trend !== undefined ? (trend > 0 ? 'up' : trend < 0 ? 'down' : 'neutral') : null;

  const cardClasses = clsx(
    'metric-card bg-white rounded-lg shadow border p-6',
    {
      'cursor-pointer hover:bg-gray-50': isClickable && !loading,
      'cursor-default': !isClickable || loading,
      'opacity-75': loading,
      'border-l-4 border-l-green-500': variant === 'success',
      'border-l-4 border-l-yellow-500': variant === 'warning',
      'border-l-4 border-l-red-500': variant === 'danger',
      'border-l-4 border-l-blue-500': variant === 'default',
    },
    className
  );

  const handleClick = () => {
    if (!loading && onClick) {
      onClick();
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (isClickable && (e.key === 'Enter' || e.key === ' ')) {
      e.preventDefault();
      handleClick();
    }
  };

  if (loading) {
    return (
      <div className={cardClasses}>
        <MetricCardSkeleton />
      </div>
    );
  }

  return (
    <div
      className={cardClasses}
      onClick={handleClick}
      role={isClickable ? 'button' : undefined}
      tabIndex={isClickable ? 0 : undefined}
      onKeyDown={handleKeyDown}
    >
      {/* Header with icon and title */}
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center space-x-3">
          {icon && (
            <div className={clsx(
              'p-3 rounded-lg',
              {
                'bg-blue-100 text-blue-600': variant === 'default',
                'bg-green-100 text-green-600': variant === 'success',
                'bg-yellow-100 text-yellow-600': variant === 'warning',
                'bg-red-100 text-red-600': variant === 'danger',
              }
            )}>
              {icon}
            </div>
          )}
          <h3 className="text-sm font-semibold text-gray-600 uppercase tracking-wide">
            {title}
          </h3>
        </div>

        {/* Trend indicator */}
        {trendDirection && (
          <TrendIndicator direction={trendDirection} value={trend!} />
        )}
      </div>

      {/* Main value */}
      <div className="mb-2">
        <p className="text-3xl font-bold text-gray-900">{value}</p>
      </div>

      {/* Subtitle */}
      {subtitle && (
        <p className="text-sm text-gray-500 mb-3">{subtitle}</p>
      )}

      {/* Trend details */}
      {trend !== undefined && (
        <div className="mt-4 pt-4 border-t border-gray-100">
          <div className="flex items-center justify-between text-sm">
            <span className="text-gray-500">vs. previous period</span>
            <span className={clsx(
              'font-semibold',
              {
                'text-green-600': trend > 0,
                'text-red-600': trend < 0,
                'text-gray-600': trend === 0,
              }
            )}>
              {trend > 0 ? '+' : ''}{trend.toFixed(1)}%
            </span>
          </div>
        </div>
      )}
    </div>
  );
};

// Trend indicator subcomponent
interface TrendIndicatorProps {
  direction: 'up' | 'down' | 'neutral';
  value: number;
  size?: 'sm' | 'md' | 'lg';
}

const TrendIndicator: React.FC<TrendIndicatorProps> = ({
  direction,
  value,
  size = 'md'
}) => {
  const iconSize = {
    sm: 'h-3 w-3',
    md: 'h-4 w-4',
    lg: 'h-5 w-5'
  }[size];

  const containerClasses = clsx(
    'flex items-center space-x-1 px-3 py-1 rounded-full text-xs font-bold',
    {
      'bg-green-100 text-green-800': direction === 'up',
      'bg-red-100 text-red-800': direction === 'down',
      'bg-gray-100 text-gray-800': direction === 'neutral',
    }
  );

  const TrendIcon = {
    up: TrendingUp,
    down: TrendingDown,
    neutral: Minus
  }[direction];

  return (
    <div className={containerClasses}>
      <TrendIcon className={iconSize} />
      <span>{Math.abs(value).toFixed(1)}%</span>
    </div>
  );
};

// Loading skeleton
const MetricCardSkeleton: React.FC = () => (
  <div className="animate-pulse">
    <div className="flex items-center justify-between mb-4">
      <div className="flex items-center space-x-3">
        <div className="w-12 h-12 bg-gray-200 rounded-lg"></div>
        <div className="h-4 bg-gray-200 rounded w-24"></div>
      </div>
      <div className="h-6 bg-gray-200 rounded-full w-16"></div>
    </div>
    <div className="h-8 bg-gray-200 rounded w-20 mb-2"></div>
    <div className="h-4 bg-gray-200 rounded w-32"></div>
  </div>
);

// Enhanced metric card with sparkline chart
interface MetricCardWithSparklineProps extends MetricCardProps {
  sparklineData?: Array<{ date: string; value: number }>;
  sparklineColor?: string;
}

export const MetricCardWithSparkline: React.FC<MetricCardWithSparklineProps> = ({
  sparklineData,
  sparklineColor = '#3B82F6',
  ...props
}) => {
  return (
    <div className="relative">
      <MetricCard {...props} />

      {sparklineData && sparklineData.length > 0 && (
        <div className="absolute bottom-4 right-4 w-16 h-8">
          <MiniSparkline
            data={sparklineData}
            color={sparklineColor}
          />
        </div>
      )}
    </div>
  );
};

// Mini sparkline component
interface MiniSparklineProps {
  data: Array<{ date: string; value: number }>;
  color: string;
}

const MiniSparkline: React.FC<MiniSparklineProps> = ({ data, color }) => {
  const maxValue = Math.max(...data.map(d => d.value));
  const minValue = Math.min(...data.map(d => d.value));
  const range = maxValue - minValue || 1;

  const points = data.map((point, index) => {
    const x = (index / (data.length - 1)) * 64; // 64px width
    const y = 32 - ((point.value - minValue) / range) * 32; // 32px height, inverted
    return `${x},${y}`;
  }).join(' ');

  return (
    <svg width="64" height="32" className="overflow-visible">
      <polyline
        fill="none"
        stroke={color}
        strokeWidth="2"
        points={points}
        className="opacity-75"
      />
    </svg>
  );
};

// Metric card variants for different use cases
export const KPICard: React.FC<MetricCardProps> = (props) => (
  <MetricCard {...props} variant="success" />
);

export const AlertCard: React.FC<MetricCardProps> = (props) => (
  <MetricCard {...props} variant="danger" />
);

export const WarningCard: React.FC<MetricCardProps> = (props) => (
  <MetricCard {...props} variant="warning" />
);

export default MetricCard;